import { Input } from '../../ui/atoms/input/Input';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../../firebase';
import { useToast } from '../../../context/ToastContext';
import { Button } from '../../ui/atoms/Button/Button';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';
import { useHandleGoogleSignIn } from '../../../hooks/useHandleGoogleSignIn';
import { useHandleAppleSignIn } from '../../../hooks/useHandleAppleSignIn';
import fetchWithAuth from '../../../utils/fetchWithAuth';
import { Capacitor } from '@capacitor/core';

export const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validEmail, setValidEmail] = useState(true);

  const navigate = useNavigate();
  const { showToast } = useToast() as any;
  const handleGoogleSignIn = useHandleGoogleSignIn();
  const handleAppleSignIn = useHandleAppleSignIn();
  const isIos = Capacitor.getPlatform() === 'ios';

  const checkEmailFormat = (email: string) => {
    setValidEmail(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  };

  const handleSubmit = async () => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const profile = await fetchWithAuth(`/api/users/${cred.user.uid}`);

      if (!profile.isEmailVerified) {
        showToast({ message: 'Please verify your email first', type: 'error' });
        navigate('/verify');
        return;
      }

      navigate('/app/home');
    } catch (err: any) {
      showToast({ message: 'Invalid email or password.', type: 'error' });
    }
  };

  const isDisabled = !email || !password || !validEmail;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      {/* ── Main fields spaced at 1rem (space-y-4) ── */}
      <div className="space-y-4">
        <Input
          onBlur={() => checkEmailFormat(email)}
          placeholder="Email"
          type="email"
          value={email}
          setValue={(v) => {
            setEmail(v);
            setValidEmail(true);
          }}
        />
        {!validEmail && (
          <div className="text-sm text-red-500">Please use valid email</div>
        )}

        <Input
          placeholder="Password"
          type="password"
          value={password}
          setValue={setPassword}
        />

        <div className="mt-4 text-sm text-black hover:underline transition">
          <span onClick={() => navigate('/forgot')}>Forgot password?</span>
        </div>
      </div>

      {/* ── Social buttons much tighter: 1px ── */}
      <div className="mt-4 space-y-2">
        <Button 
          disabled={isDisabled} 
          variant="black" 
          className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
          onClick={handleSubmit}>
          Sign in
        </Button>
        <Button
          variant="transparent"
          onClick={async () => {
            try {
              await handleGoogleSignIn();
            } catch (err: any) {
              console.error('Google sign-in failed:', err);
              showToast?.({ message: err?.message ?? 'Google sign-in failed', type: 'error' });
            }
          }}
        >
          <FcGoogle className="text-xl"/>
          <span className="font-large text-sm">Sign in with Google</span>
        </Button>

        {isIos && ( 
          <Button
            variant="transparent"
            className="text-white bg-black hover:bg-gray-600"
            onClick={async () => {
              try {
                await handleAppleSignIn();
              } catch (err: any) {
                showToast({ message: err.message, type: 'error' });
              }
            }}
          >
            <FaApple className="text-xl" />
            <span className="ml-2 font-medium text-sm">Sign in with Apple</span>
          </Button> 
        )}
      </div>
    </form>
  );
};